import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { ResetPasswordFormContent } from "modules/Login/components/ResetPassword/components";
import { useResetPasswordMutation } from "modules/Login/components/ResetPassword/hooks";
import "modules/Login/components/ResetPassword/ResetPassword.scss";

export type ResetPasswordFormPayload = {
  newPassword: string;
  newPasswordConfirmation: string;
};

export const PASSWORD_STRENGTH_THRESHOLD = 4;

const FormHelper = createFormHelper<ResetPasswordFormPayload>();

export const ResetPassword = () => {
  const { t } = useTranslation();

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<number | null>(null);

  const handleSetErrorMsg = (message: string) => setErrorMsg(message);

  const { mutate: resetPassword, isPending } = useResetPasswordMutation(handleSetErrorMsg);

  const location = useLocation();
  const resetHash = new URLSearchParams(location.search).get("hash");

  const validateResetPasswordForm = ({ newPassword, newPasswordConfirmation }: ResetPasswordFormPayload) => {
    if (!newPassword || !newPasswordConfirmation) {
      setErrorMsg(t("v8_fill_passowrd_confirm_password", "Fill in password and confirm password"));
      return false;
    }

    if (newPassword !== newPasswordConfirmation) {
      setErrorMsg(t("v8_passwords_do_not_match", "Passwords do not match"));
      return false;
    }

    if (!passwordStrength || passwordStrength < PASSWORD_STRENGTH_THRESHOLD) {
      setErrorMsg(t("v8_choose_stronger_password", "Please choose a stronger password"));
      return false;
    }

    return true;
  };

  const handleSetPasswordStrength = (strength: number | null) => setPasswordStrength(strength);

  const onSubmit = (resetPasswordPayload: ResetPasswordFormPayload) => {
    if (!validateResetPasswordForm(resetPasswordPayload) || !resetHash) return;

    resetPassword(
      { hash: resetHash, new_password: resetPasswordPayload.newPassword, new_password_confirm: resetPasswordPayload.newPasswordConfirmation },
      { onSuccess: () => setSuccessMsg(t("v8_password_successfully_reset", "Your password was successfully reset!")) }
    );
  };

  return (
    <div className="ResetPassword">
      <div className="header">
        <p>{t("v8_reset_password", "Reset Password")}</p>
        <Link to={`/login`} onClick={() => setSuccessMsg("")} className="backBtn">
          <Icon className="angle left" />
          {t("v8_back_to_login", "Back to Login")}
        </Link>
      </div>
      <div className="mt-15">
        {!successMsg ? (
          <p>
            {t("v8_reset_password_description") ||
              "Your new password must be secure. A secure password cannot contain your first name, last name, email, Claire' or common words like 'password' You don't need to use any special characters. A secure password contains two or more uncommon words."}
          </p>
        ) : (
          <div className="success">{successMsg}</div>
        )}
        <FormHelper.Form defaultValues={{ newPassword: "", newPasswordConfirmation: "" }} onSubmit={onSubmit}>
          {passwordStrength !== null && !successMsg && (
            <div className="strength-meter">
              <div className="strength-meter-fill" data-strength={passwordStrength}></div>
            </div>
          )}
          <ResetPasswordFormContent
            errorMsg={errorMsg}
            resetHash={resetHash}
            isPending={isPending}
            handleSetErrorMsg={handleSetErrorMsg}
            handleSetPasswordStrength={handleSetPasswordStrength}
          />
        </FormHelper.Form>
      </div>
    </div>
  );
};
